/**
 * ScanPanel - run a Legislation Scan on a document and watch findings stream in.
 *
 * Self-contained (local state + PubSub): pick which sources to scan against
 * (reusing the research SourceTree so jurisdiction indexes and data sources are
 * selected exactly as in Research), start the scan for the given target, and
 * render findings live as the engine flags them. Reusable from a draft, an
 * uploaded file, or a review-session document.
 *
 * Uses the app's own design system throughout - Button, BrandLoader (the logo
 * animation), brand colour tokens, and the shared feedback-modal for messages
 * (never toasts). RTL-safe and fully translated.
 */

import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  PiScalesLight, PiWarningLight, PiLinkSimpleLight,
  PiCheckCircleLight, PiXLight,
} from 'react-icons/pi';
import { useAppDispatch } from '../../store';
import { showFeedbackModal } from '../../store/actions/UserFeedbackActions';
import Button from '../shared/TailwindComponents/Button';
import BrandLoader from '../shared/brand/BrandLoader';
import {
  startDraftScan, startFileScan, startReviewDocumentScan, getScanFindings,
  getLatestScanForSource, applyScanFindingSuggestion,
  ScanFinding, ScanSeverity, StartScanOptions,
} from '../../utils/scanApi';
import { useScanPubSub } from './useScanPubSub';
import SourceTree from '../Research/SourceTree';
import { toSourceSelection } from '../Research/sources';
import type { ResearchSource } from '../../store/actions/ChatAction';

export type ScanTarget =
  | { kind: 'draft'; draftUuid: string }
  | { kind: 'file'; fileUuid: string }
  | { kind: 'reviewDocument'; sessionUuid: string; documentId: number };

export interface ScanPanelProps {
  target: ScanTarget;
  jurisdictionSources?: ResearchSource[];
  userDatasources?: ResearchSource[];
  defaultSelected?: ResearchSource[];
  language?: string | null;
  onClose?: () => void;
}

const SEVERITY_ORDER: ScanSeverity[] = ['critical', 'major', 'minor', 'info'];

// Match the Review surface's severity tones (danger / warning / info scales).
const SEVERITY_CLASSES: Record<ScanSeverity, string> = {
  critical: 'bg-danger-50 text-danger-700 border border-danger-200',
  major: 'bg-warning-50 text-warning-900 border border-warning-200',
  minor: 'bg-warning-50 text-warning-900 border border-warning-200',
  info: 'bg-info-50 text-info-700 border border-info-200',
};

function severityRank(s: ScanSeverity): number {
  const i = SEVERITY_ORDER.indexOf(s);
  return i < 0 ? 99 : i;
}

const ScanPanel: React.FC<ScanPanelProps> = ({
  target,
  jurisdictionSources = [],
  userDatasources = [],
  defaultSelected = [],
  language = null,
  onClose,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const [selected, setSelected] = useState<ResearchSource[]>(defaultSelected);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [scanUuid, setScanUuid] = useState<string | null>(null);
  const [status, setStatus] = useState<'idle' | 'starting' | 'running' | 'completed' | 'failed' | 'stalled'>('idle');
  const [findings, setFindings] = useState<ScanFinding[]>([]);
  const [applyingId, setApplyingId] = useState<string | null>(null);
  const [appliedIds, setAppliedIds] = useState<Set<string>>(new Set());

  // How this scan target is keyed on ScanRun.source_document_uuid - so we can
  // resurface a prior scan for this exact document after a refresh.
  const sourceRef =
    target.kind === 'draft' ? target.draftUuid
      : target.kind === 'file' ? target.fileUuid
        : `${target.sessionUuid}#${target.documentId}`;

  const notify = useCallback((modalType: 'success' | 'error' | 'warning', message: string) => {
    dispatch(showFeedbackModal({ showModal: true, modalType, message, duration: 6000 }));
  }, [dispatch]);

  // On open, load the latest scan for this document (survives refresh). If it's
  // finished, show its findings; if still running, resume polling; else idle.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { scan, findings: prior } = await getLatestScanForSource(sourceRef);
        if (cancelled || !scan) return;
        if (prior?.length) setFindings(prior);
        if (scan.status === 'completed' || scan.status === 'completed_with_errors') {
          setScanUuid(scan.uuid); setStatus('completed');
        } else if (scan.status === 'failed') {
          setScanUuid(scan.uuid); setStatus('failed');
        } else {
          // pending/processing - resume only if it's a FRESH run. A run stuck
          // pending for >15 min is orphaned (worker crash); don't resume-poll it
          // forever - leave the panel idle so the user can start a new scan.
          const started = scan.started_at ? new Date(scan.started_at).getTime() : 0;
          const ageMin = started ? (Date.now() - started) / 60000 : Infinity;
          if (ageMin <= 15) {
            setScanUuid(scan.uuid); setStatus('running');
          } else {
            // Orphaned run (worker crash / never completed). Show it honestly as
            // stalled rather than idle - a stale "processing" run is NOT a clean
            // "no issues" result, and the user needs a clear way to re-run.
            setScanUuid(scan.uuid); setStatus('stalled');
          }
        }
      } catch {
        // no prior scan / transient - stay idle
      }
    })();
    return () => { cancelled = true; };
  }, [sourceRef]);

  const handleFinding = useCallback((f: ScanFinding) => {
    setFindings((prev) => (prev.some((p) => p.uuid === f.uuid) ? prev : [...prev, f]));
  }, []);

  const handleCompleted = useCallback((payload: { status: string }) => {
    setStatus(payload.status === 'failed' ? 'failed' : 'completed');
  }, []);

  // Live path: PubSub (fast). Runs when the socket connects.
  useScanPubSub({
    scanUuid,
    enabled: !!scanUuid && (status === 'running' || status === 'starting'),
    onFinding: handleFinding,
    onCompleted: handleCompleted,
  });

  // Reliable fallback: poll the read API. Findings + status are persisted by the
  // engine regardless of PubSub, so this guarantees the panel shows results and
  // completion even if the socket never connects. Stops once terminal.
  useEffect(() => {
    if (!scanUuid || !(status === 'running' || status === 'starting')) return;
    let cancelled = false;
    let attempts = 0;
    const MAX_ATTEMPTS = 60; // ~4 min at 4s - backstop so a stuck run can't loop forever
    let timer: ReturnType<typeof setTimeout>;
    const poll = async () => {
      attempts += 1;
      try {
        const { scan, findings: fetched } = await getScanFindings(scanUuid);
        if (cancelled) return;
        if (fetched?.length) {
          // Only push genuinely new findings, and only replace state when the
          // set actually grew - returning `prev` unchanged avoids a re-render on
          // every 4s tick when nothing new has landed (PubSub usually delivered
          // it first). This is the main re-render-churn fix.
          setFindings((prev) => {
            const seen = new Set(prev.map((p) => p.uuid));
            const added = fetched.filter((f) => !seen.has(f.uuid));
            return added.length ? [...prev, ...added] : prev;
          });
        }
        if (scan?.status === 'completed' || scan?.status === 'completed_with_errors' || scan?.status === 'failed') {
          setStatus((s) => (s === (scan.status === 'failed' ? 'failed' : 'completed')
            ? s
            : (scan.status === 'failed' ? 'failed' : 'completed')));
          return; // terminal - stop polling
        }
      } catch {
        // transient - keep polling
      }
      if (!cancelled && attempts < MAX_ATTEMPTS) {
        timer = setTimeout(poll, 4000);
      } else if (!cancelled) {
        // Gave up before the run reached a terminal state - it's stalled, not
        // clean. Don't leave an infinite spinner or imply "no issues".
        setStatus('stalled');
      }
    };
    // Fire the first poll almost immediately so findings/status appear fast
    // (PubSub is primary; this makes the backstop feel instant too), then settle
    // into the 4s cadence.
    timer = setTimeout(poll, 800);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [scanUuid, status]);

  const hasSources = jurisdictionSources.length > 0 || userDatasources.length > 0;

  const buildOptions = (): StartScanOptions => ({
    selected_sources: toSourceSelection(selected),
    language,
  });

  const startScan = async () => {
    if (selected.length === 0) {
      notify('warning', t('Select at least one source to scan against'));
      return;
    }
    setStatus('starting');
    setFindings([]);
    try {
      const opts = buildOptions();
      let result;
      if (target.kind === 'draft') result = await startDraftScan(target.draftUuid, opts);
      else if (target.kind === 'file') result = await startFileScan(target.fileUuid, opts);
      else result = await startReviewDocumentScan(target.sessionUuid, target.documentId, opts);

      if (result.error || !result.scan_run_uuid) {
        setStatus('failed');
        notify('error', result.error || t('Could not start the scan'));
        return;
      }
      setScanUuid(result.scan_run_uuid);
      setStatus('running');
    } catch (e: any) {
      setStatus('failed');
      notify('error', e?.response?.data?.error || t('Could not start the scan'));
    }
  };

  const sortedFindings = useMemo(
    () => [...findings].sort((a, b) => {
      const r = severityRank(a.severity) - severityRank(b.severity);
      if (r !== 0) return r;
      return (b.risk_score || 0) - (a.risk_score || 0);
    }),
    [findings],
  );

  const counts = useMemo(() => {
    const c: Record<ScanSeverity, number> = { critical: 0, major: 0, minor: 0, info: 0 };
    findings.forEach((f) => { if (c[f.severity] !== undefined) c[f.severity] += 1; });
    return c;
  }, [findings]);

  const isRunning = status === 'running' || status === 'starting';

  // Return to the source-selection screen so a new scan can be started. A scan
  // can be run as many times as the user wants - a prior run never locks the
  // panel. Clears the resurfaced run so its findings don't bleed into the next.
  const startNewScan = useCallback(() => {
    setScanUuid(null);
    setFindings([]);
    setStatus('idle');
  }, []);

  const copySuggestion = useCallback((text: string) => {
    try { navigator.clipboard?.writeText(text); notify('success', t('Suggested change copied')); }
    catch { notify('warning', t('Could not copy — select and copy manually')); }
  }, [notify, t]);

  // Apply a finding's suggested redline into the draft. Server-side, saved as a
  // NEW (restorable) version - never a destructive overwrite. Only offered for
  // draft targets; on a stale finding the backend reports applied=false.
  const applySuggestion = useCallback(async (f: ScanFinding) => {
    if (!scanUuid) return;
    setApplyingId(f.uuid);
    try {
      const res = await applyScanFindingSuggestion(scanUuid, f.uuid);
      if (res.applied) {
        setAppliedIds((prev) => new Set(prev).add(f.uuid));
        notify('success', t('Change applied to the draft (a new version was saved)'));
      } else if (res.reason === 'flagged_text_not_found') {
        notify('warning', t('The document has changed since this scan — re-scan, then apply.'));
      } else {
        notify('warning', t('Nothing to apply for this finding.'));
      }
    } catch {
      notify('error', t('Could not apply the change. Please try again.'));
    } finally {
      setApplyingId(null);
    }
  }, [scanUuid, notify, t]);

  return (
    <div className="flex flex-col h-full bg-surface text-fg-body text-start" dir="auto">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-hairline">
        <div className="flex items-center gap-2">
          <PiScalesLight className="text-primary-600" size={20} />
          <h2 className="text-base font-semibold text-fg-strong">{t('Legislation scan')}</h2>
        </div>
        <div className="flex items-center gap-2">
          {!isRunning && status !== 'idle' && (
            <Button variant="outline" size="small" onClick={startNewScan} icon={<PiScalesLight size={14} />}>
              {t('New scan')}
            </Button>
          )}
          {onClose && (
            <Button
              variant="neutral"
              size="small"
              onClick={onClose}
              ariaLabel={t('Close')}
              icon={<PiXLight size={18} />}
            />
          )}
        </div>
      </div>

      {/* Source selection */}
      {status === 'idle' && (
        <div className="flex flex-col flex-1 min-h-0">
          <div className="px-4 py-3">
            <p className="text-sm text-fg-muted">
              {t('Scan this document against legislation and flag conflicts, repealed references, and jurisdiction mismatches.')}
            </p>
          </div>
          <div className="flex-1 overflow-y-auto px-4">
            {hasSources ? (
              <SourceTree
                jurisdictionSources={jurisdictionSources}
                userDatasources={userDatasources}
                selected={selected}
                onChange={setSelected}
                expanded={expanded}
                onToggleExpanded={(code) =>
                  setExpanded((prev) => ({ ...prev, [code]: prev[code] === false }))
                }
              />
            ) : (
              <p className="text-sm text-warning-900 bg-warning-50 border border-warning-200 rounded-md p-3">
                {t('No scan sources are configured. Add a jurisdiction index or a data source first.')}
              </p>
            )}
          </div>
          <div className="px-4 py-3 border-t border-surface-sunken">
            <Button
              variant="primary"
              size="medium"
              onClick={startScan}
              disabled={selected.length === 0}
              icon={<PiScalesLight size={16} />}
              className="w-full justify-center"
            >
              {t('Run scan')}
            </Button>
          </div>
        </div>
      )}

      {/* Running / results */}
      {status !== 'idle' && (
        <div className="flex flex-col flex-1 min-h-0">
          <div className="px-4 py-2 border-b border-surface-sunken flex items-center gap-3 text-sm">
            {isRunning ? (
              <span className="flex items-center gap-2 text-fg-body">
                <BrandLoader size="glyph" tone="brand" />
                {t('Scanning…')}
              </span>
            ) : status === 'completed' ? (
              <span className="flex items-center gap-2 text-success-700">
                <PiCheckCircleLight size={18} />
                {t('Scan complete')}
              </span>
            ) : status === 'stalled' ? (
              <span className="flex items-center gap-2 text-warning-600">
                <PiWarningLight size={18} />
                {t('Scan didn’t finish')}
              </span>
            ) : (
              <span className="flex items-center gap-2 text-danger-600">
                <PiWarningLight size={18} />
                {t('Scan failed')}
              </span>
            )}
            <span className="ms-auto flex items-center gap-1.5">
              {SEVERITY_ORDER.map((s) => counts[s] > 0 && (
                <span key={s} className={`px-1.5 py-0.5 rounded text-xs ${SEVERITY_CLASSES[s]}`}>
                  {counts[s]} {t(s)}
                </span>
              ))}
            </span>
          </div>

          <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
            {sortedFindings.length === 0 && isRunning && (
              <div className="flex flex-col items-center justify-center py-10">
                <BrandLoader size="lg" tone="brand" label={t('Scanning…')} />
                <p className="mt-3 text-sm text-fg-muted">{t('Findings appear here as each document is read.')}</p>
              </div>
            )}
            {sortedFindings.length === 0 && status === 'completed' && (
              <p className="text-sm text-fg-muted">{t('No issues were flagged.')}</p>
            )}
            {sortedFindings.length === 0 && status === 'stalled' && (
              <div className="flex flex-col items-start gap-2 py-4">
                <p className="text-sm text-fg-muted">
                  {t('This scan didn’t finish — the run may have stalled. Start a new scan to try again.')}
                </p>
                <Button variant="outline" size="small" onClick={startNewScan}>
                  {t('Run scan again')}
                </Button>
              </div>
            )}
            {sortedFindings.length === 0 && status === 'failed' && (
              <p className="text-sm text-danger-600">{t('The scan failed to run. Please try again.')}</p>
            )}
            {sortedFindings.map((f) => {
              const leg = f.legislation || f.legislation_cited || {};
              return (
                <div key={f.uuid} className="border border-hairline-soft rounded-md p-3 bg-surface-sunken/70">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${SEVERITY_CLASSES[f.severity]}`}>
                      {t(f.severity)}
                    </span>
                    {f.finding_type && (
                      <span className="text-xs text-fg-muted">{t(f.finding_type)}</span>
                    )}
                    {f.section_reference && (
                      <span className="text-xs text-fg-muted ms-auto">{f.section_reference}</span>
                    )}
                  </div>
                  {f.issue_description && (
                    <p className="text-sm text-fg-body mb-1">{f.issue_description}</p>
                  )}
                  {f.flagged_text_span && (
                    <blockquote className="text-xs text-fg-muted border-s-2 border-surface-sunken ps-2 my-1 italic">
                      {f.flagged_text_span}
                    </blockquote>
                  )}
                  {f.suggested_redline && (
                    <div className="mt-2 rounded-md border border-success-200 bg-success-50 p-2">
                      <div className="text-[11px] font-medium text-success-700 mb-1">
                        {t('Suggested change')}
                      </div>
                      <p className="text-xs text-fg-body whitespace-pre-wrap">{f.suggested_redline}</p>
                      <div className="flex items-center gap-2 mt-2">
                        <Button
                          variant="outline"
                          size="small"
                          onClick={() => copySuggestion(f.suggested_redline || '')}
                        >
                          {t('Copy')}
                        </Button>
                        {target.kind === 'draft' && (
                          appliedIds.has(f.uuid) ? (
                            <span className="text-xs text-success-700 inline-flex items-center gap-1">
                              <PiCheckCircleLight size={14} /> {t('Applied')}
                            </span>
                          ) : (
                            <Button
                              variant="primary"
                              size="small"
                              onClick={() => applySuggestion(f)}
                              isLoading={applyingId === f.uuid}
                            >
                              {t('Apply to draft')}
                            </Button>
                          )
                        )}
                      </div>
                    </div>
                  )}
                  {(leg.title || leg.document_number) && (
                    <div className="text-xs text-fg-muted mt-1 flex items-center gap-1 flex-wrap">
                      <PiScalesLight size={12} />
                      <span>{leg.title}{leg.document_number ? ` (${leg.document_number})` : ''}</span>
                      {leg.in_force === false && (
                        <span className="px-1 rounded bg-danger-50 text-danger-700 border border-danger-200">{t('repealed / superseded')}</span>
                      )}
                      {leg.in_force === true && (
                        <span className="px-1 rounded bg-success-50 text-success-700 border border-success-200">{t('in force')}</span>
                      )}
                      {leg.external_url && /^https?:\/\//i.test(leg.external_url) && (
                        <a href={leg.external_url} target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:text-primary-700 inline-flex items-center gap-0.5">
                          <PiLinkSimpleLight size={12} />{t('source')}
                        </a>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default ScanPanel;
